import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';

import { ApiController, ApiExecutor, ApiResponse, ApiValidationService, Body, DELETE, GET, Header, PATCH, Params, POST, PUT, Query, PaginatedResult } from '@app/core/api';

import {
  DeactivateUserDto,
  UpdateCreditLackDto,
  UpdatePasswordDto,
  UserFeedbackDto,
} from './dto';
import type {
  UserCreditLackResult,
  UserLineMagfaWhoisResult,
  UserLineReadyResult,
  UserLoginLogResult,
  UserLoginSettingResult,
  UserResult,
} from './user-api.models';
import type {PlanDowngradeResult, PlanPackageResult} from '@app/main/modules/plans/api/plan-api.models';

@ApiController('api/app/user')
@Injectable({
  providedIn: 'root',
})
export class UserApiService {
  constructor(
    public apiExecutor: ApiExecutor,
    public apiValidationService: ApiValidationService,
  ) {}

  @GET()
  getUser(): Observable<ApiResponse<UserResult>> {
    return {} as Observable<ApiResponse<UserResult>>;
  }

  @PUT('/business-profile')
  updateBusinessProfile(@Body() formData: FormData): Observable<ApiResponse<boolean>> {
    return {} as Observable<ApiResponse<boolean>>;
  }

  @DELETE('/image')
  deleteUserImage(): Observable<ApiResponse<boolean>> {
    return {} as Observable<ApiResponse<boolean>>;
  }

  @POST('/image')
  updateUserImage(@Body() formData: FormData): Observable<ApiResponse<string>> {
    return {} as Observable<ApiResponse<string>>;
  }

  @GET('/login-logs')
  getLoginLogs(@Query() query: Record<string, unknown>): Observable<ApiResponse<PaginatedResult<UserLoginLogResult>>> {
    return {} as Observable<ApiResponse<PaginatedResult<UserLoginLogResult>>>;
  }

  @POST('/email-verification')
  @Header({'Content-Type': 'application/json'})
  emailVerification(@Body() email: string): Observable<ApiResponse<boolean>> {
    return {} as Observable<ApiResponse<boolean>>;
  }

  @POST('/password')
  @Header({'Content-Type': 'application/json'})
  createPassword(@Body() password: string): Observable<ApiResponse<boolean>> {
    return {} as Observable<ApiResponse<boolean>>;
  }

  @PUT('/password')
  updatePassword(@Body() dto: UpdatePasswordDto): Observable<ApiResponse<boolean>> {
    return {} as Observable<ApiResponse<boolean>>;
  }

  @DELETE()
  deactivate(@Body() dto: DeactivateUserDto): Observable<ApiResponse<boolean>> {
    return {} as Observable<ApiResponse<boolean>>;
  }

  @GET('/credit-lack-warning-setting')
  getCreditLack(): Observable<ApiResponse<UserCreditLackResult>> {
    return {} as Observable<ApiResponse<UserCreditLackResult>>;
  }

  @PUT('/credit-lack-warning-setting')
  updateCreditLack(@Body() dto: UpdateCreditLackDto): Observable<ApiResponse<boolean>> {
    return {} as Observable<ApiResponse<boolean>>;
  }

  @POST('/login-setting/valid-mobile')
  @Header({'Content-Type': 'application/json'})
  createValidMobile(@Body() mobile: string): Observable<ApiResponse<boolean>> {
    return {} as Observable<ApiResponse<boolean>>;
  }

  @DELETE('/login-setting/valid-mobile')
  @Header({'Content-Type': 'application/json'})
  deleteValidMobile(@Body() mobile: string): Observable<ApiResponse<boolean>> {
    return {} as Observable<ApiResponse<boolean>>;
  }

  @POST('/login-setting/valid-ip')
  @Header({'Content-Type': 'application/json'})
  createValidIp(@Body() ip: string): Observable<ApiResponse<boolean>> {
    return {} as Observable<ApiResponse<boolean>>;
  }

  @DELETE('/login-setting/valid-ip')
  @Header({'Content-Type': 'application/json'})
  deleteValidIp(@Body() ip: string): Observable<ApiResponse<boolean>> {
    return {} as Observable<ApiResponse<boolean>>;
  }

  @GET('/login-setting')
  getLoginSetting(): Observable<ApiResponse<UserLoginSettingResult>> {
    return {} as Observable<ApiResponse<UserLoginSettingResult>>;
  }

  @PUT('/login-setting/has-two-step-login')
  updateHasTwoStepLogin(@Body() hasTwoStepLogin: boolean): Observable<ApiResponse<boolean>> {
    return {} as Observable<ApiResponse<boolean>>;
  }

  @GET('/line/:id/magfa-whois')
  getLineMagfaWhois(@Params('id') id: number): Observable<ApiResponse<boolean>> {
    return {} as Observable<ApiResponse<boolean>>;
  }

  @POST('/line/:id/magfa-whois')
  verifyLineMagfaWhois(@Params('id') id: number): Observable<ApiResponse<UserLineMagfaWhoisResult>> {
    return {} as Observable<ApiResponse<UserLineMagfaWhoisResult>>;
  }

  @PATCH('/line/:id/magfa-whois')
  @Header({'Content-Type': 'application/json'})
  verifyLineMagfaWhoisOtp(
    @Params('id') id: number,
    @Body() otp: string,
  ): Observable<ApiResponse<UserLineMagfaWhoisResult>> {
    return {} as Observable<ApiResponse<UserLineMagfaWhoisResult>>;
  }

  @POST('/line/:id/pishgamrayan-whois')
  activateLinePishgamrayanWhois(@Params('id') id: number): Observable<ApiResponse<boolean>> {
    return {} as Observable<ApiResponse<boolean>>;
  }

  @GET('/line/ready')
  getLineReady(@Query() query: Record<string, unknown>): Observable<ApiResponse<UserLineReadyResult<number | null>>> {
    return {} as Observable<ApiResponse<UserLineReadyResult<number | null>>>;
  }

  @POST('/feedback')
  sendFeedback(@Body() dto: UserFeedbackDto): Observable<ApiResponse<unknown>> {
    return {} as Observable<ApiResponse<unknown>>;
  }

  @PATCH('/confirmation')
  @Header({'Content-Type': 'application/json'})
  userConfirmation(@Body() confirmation: string): Observable<ApiResponse<unknown>> {
    return {} as Observable<ApiResponse<unknown>>;
  }

  @GET('/me/plan/package')
  getPlanPackages(): Observable<ApiResponse<PlanPackageResult[]>> {
    return {} as Observable<ApiResponse<PlanPackageResult[]>>;
  }

  @GET('/me/plan/package/:planPackageId/downgrade/check')
  checkDowngrade(@Params('planPackageId') planPackageId: number): Observable<ApiResponse<PlanDowngradeResult>> {
    return {} as Observable<ApiResponse<PlanDowngradeResult>>;
  }
}
